/**
 * CommandPalette — Cmd+K global search & actions
 * Uses cmdk v1 wrapped in a Radix Dialog for the modal.
 * Incluye búsqueda semántica AI (embeddings) para tareas + recursos.
 */
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import {
  Search, List, Users, Plus, Zap,
  ArrowRight, FileText, FolderOpen, Camera, FileBarChart,
  Sparkles, Loader2,
} from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";
import { api } from "../api/client.js";
import { flattenNavItems, matchesRole } from "../nav/registry.js";
import { useRolNavigation } from "../lib/useRolNavigation.js";
import { useOSBadges } from "../lib/useOSBadges.js";
import { useAuth } from "../context/AuthContext.jsx";
import { trackNav } from "../lib/nav-tracker.js";
import { useFlag } from "../lib/useFlags.js";
import { useClient } from "../context/ClientContext.jsx";
import { parseCrearTarea } from "../lib/parseCrearTarea.js";

const ACTION_ITEMS = [
  { label: "Nueva tarea",                 icon: Plus,         action: "new-task" },
  { label: "Nuevo cliente",               icon: Users,        action: "new-cliente" },
  { label: "Nuevo proyecto",              icon: FolderOpen,   action: "new-proyecto" },
  { label: "Nueva cobertura",             icon: Camera,       href: "/coberturas?new=1" },
  { label: "Importar plan de contenidos", icon: FileText,     action: "import-plan" },
  { label: "Generar reporte mensual",     icon: FileBarChart, action: "generate-report" },
  { label: "Compilar deck de cliente",    icon: Zap,          action: "compile-deck" },
];

/* ── Component ──────────────────────────────────────────────────────────── */
export function CommandPalette({ open, onOpenChange }) {
  const navigate  = useNavigate();
  const { miembro, rol } = useAuth();
  const nav = useRolNavigation();
  const { badges } = useOSBadges();
  const [query, setQuery] = useState("");
  const [semanticResults, setSemanticResults] = useState([]);
  const [textoResults, setTextoResults] = useState([]);   // buscador completo (texto exacto en todo el sistema)
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  // ⌘K que crea (brand book §8): si el texto no es una ruta, ofrece «Crear tarea» con chips deducidos.
  const crearOn = useFlag("ff_cmdk_crear");
  const { clientes = [] } = useClient();
  const [equipoLista, setEquipoLista] = useState([]);
  useEffect(() => {
    if (!open || !crearOn || equipoLista.length) return;
    api.getEquipo().then(r => setEquipoLista(r?.equipo || [])).catch(() => {});
  }, [open, crearOn]);
  const propuesta = useMemo(
    () => (crearOn && query.trim().length >= 3 ? parseCrearTarea(query, { clientes, equipo: equipoLista }) : null),
    [crearOn, query, clientes, equipoLista]);

  // Rol combinado (rol + titulo + roles_extra) — mismo criterio que Layout.
  const allRolesStr = useMemo(() => {
    const partes = [];
    if (rol) partes.push(String(rol));
    if (miembro?.titulo) partes.push(String(miembro.titulo));
    let extras = miembro?.roles_extra;
    if (typeof extras === "string") { try { extras = JSON.parse(extras); } catch { extras = []; } }
    if (Array.isArray(extras)) partes.push(...extras.map(String));
    return partes.join(" ");
  }, [rol, miembro?.titulo, miembro?.roles_extra]);

  // Lista plana del registry filtrada por rol.
  const navItems = useMemo(() => {
    const all = flattenNavItems(nav, badges);
    return all.filter(item => !item.roles || matchesRole(allRolesStr, item.roles));
  }, [nav, badges, allRolesStr]);

  // Grupos en orden de aparición
  const groupsInOrder = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const it of navItems) {
      if (!seen.has(it.group)) { seen.add(it.group); out.push(it.group); }
    }
    return out;
  }, [navItems]);

  /* Buscador completo por texto (tareas, comentarios, coberturas, pedidos, clientes, grupos, equipo) — desde 2 caracteres */
  useEffect(() => {
    if (!query || query.trim().length < 2) { setTextoResults([]); return; }
    const t = setTimeout(() => {
      api.search(query.trim()).then(r => setTextoResults((r.results || []).slice(0, 12))).catch(() => setTextoResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const IR_TEXTO = {
    tarea: r => `/tareas?focus=${r.id}`, comentario: r => `/tareas?focus=${r.id}&tab=chat`, cobertura: r => `/coberturas-v2?cobertura=${r.id}`,
    pedido: () => "/pedidos-cliente", cliente: r => `/clientes/${r.id}`, grupo: r => `/tareas?fCliente=${r.cliente_id || ""}`, equipo: r => `/equipo/${r.id}`, factura: () => "/facturacion",
  };
  const ETIQUETA_TEXTO = { tarea: "Tarea", comentario: "Comentario", cobertura: "Cobertura", pedido: "Pedido", cliente: "Cliente", grupo: "Grupo", equipo: "Persona", factura: "Factura" };

  /* Debounced search (semántica + respaldo por texto en el backend) — desde 3 caracteres */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 3) {
      setSemanticResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.semanticSearch(query, { limit: 6 });
        setSemanticResults(res.results || []);
      } catch (e) {
        setSemanticResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  function runCommand(fn) {
    onOpenChange(false);
    setQuery("");
    setSemanticResults([]);
    setTextoResults([]);
    fn();
  }

  function handleOpenChange(val) {
    if (!val) { setQuery(""); setSemanticResults([]); }
    onOpenChange(val);
  }

  function openSemanticResult(r) {
    if (r.entidad === "tarea") {
      runCommand(() => navigate(`/tareas?abrir=${r.entidad_id}`));
    } else if (r.entidad === "cliente") {
      runCommand(() => navigate(`/clientes/${r.entidad_id}`));
    } else if (r.entidad === "proyecto") {
      runCommand(() => navigate(`/proyectos?abrir=${r.entidad_id}`));
    } else {
      runCommand(() => {});
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[#0B0D10]/45 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className="fixed z-50 outline-none data-[state=open]:animate-slide-up max-sm:inset-0 max-sm:h-full sm:left-1/2 sm:top-[18%] sm:w-full sm:max-w-xl sm:-translate-x-1/2"
          aria-label="Paleta de comandos"
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">Paleta de comandos</Dialog.Title>
          <Command
            className="overflow-hidden bg-card shadow-[0_40px_100px_rgba(17,19,24,0.28)] max-sm:h-full max-sm:flex max-sm:flex-col sm:rounded-[28px]"
            shouldFilter={true}
            loop
          >
            {/* Search input row */}
            <div className="flex items-center gap-3 border-b border-[#F2F3F6] px-5 max-sm:pt-[max(8px,env(safe-area-inset-top))] flex-shrink-0">
              <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar tareas, clientes, recursos o acciones…"
                className={cn(
                  "flex h-14 w-full bg-transparent text-[17px] font-semibold text-foreground",
                  "placeholder:text-muted-foreground outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50"
                )}
              />
              {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-accent flex-shrink-0" />}
              <kbd className="hidden sm:inline flex-shrink-0 text-[12px] text-muted-foreground/60 border border-border/40 rounded-md px-1.5 py-0.5 bg-muted/30 font-mono">
                ESC
              </kbd>
              <button type="button" onClick={() => onOpenChange(false)} aria-label="Cerrar" className="sm:hidden h-11 w-11 -mr-2 rounded-full flex items-center justify-center text-muted-foreground active:bg-muted flex-shrink-0">
                <span className="msr text-[24px]" aria-hidden="true">close</span>
              </button>
            </div>

            {/* Results */}
            <Command.List className="max-h-[26rem] overflow-y-auto p-2 max-sm:max-h-none max-sm:flex-1 max-sm:pb-[max(16px,env(safe-area-inset-bottom))]">
              {/* cmdk no cuenta los ítems con forceMount (búsqueda IA, «Crear tarea»): sin este chequeo mostraba
                  «Sin resultados» encima de resultados reales. */}
              {!propuesta && semanticResults.length === 0 && (
                <Command.Empty className="py-10 text-center text-sm text-muted-foreground">
                  Sin resultados para &ldquo;{query}&rdquo;
                </Command.Empty>
              )}

              {propuesta && (
                <Command.Group heading="Crear" className={groupCls} forceMount>
                  <Command.Item
                    forceMount
                    value={`__crear ${query}`}
                    onSelect={() => runCommand(() => {
                      // Enter abre CrearTareaModal precompletado: nunca crea en silencio.
                      window.__crewPrefill = {
                        nombre: propuesta.titulo, cliente_id: propuesta.cliente?.id || "", tipo_contenido: propuesta.tipo || "",
                        deadline: propuesta.fecha || "", asignar_a: propuesta.responsable?.id || "",
                      };
                      window.dispatchEvent(new CustomEvent("crew-action", { detail: "new-task" }));
                    })}
                    className={itemCls}
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-md bg-accent/15 ring-1 ring-accent/20 flex-shrink-0">
                      <Plus className="h-3 w-3 text-accent" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">Crear tarea «{propuesta.titulo}»</span>
                      <span className="mt-1 flex flex-wrap gap-1.5 text-[12px]">
                        {[propuesta.cliente?.nombre, propuesta.tipo, propuesta.fecha, propuesta.responsable?.nombre, "1 crédito"].filter(Boolean).map(c => (
                          <span key={c} className="rounded-full bg-muted px-2 py-0.5 font-bold text-muted-foreground">{c}</span>
                        ))}
                      </span>
                    </span>
                  </Command.Item>
                </Command.Group>
              )}

              {/* Buscador completo: texto en todo el sistema */}
              {textoResults.length > 0 && (
                <Command.Group heading="En todo el sistema" className={groupCls} forceMount>
                  {textoResults.map((r, i) => (
                    <Command.Item
                      key={`txt-${r.tipo}-${r.id}-${i}`}
                      value={`__texto_${r.tipo}_${r.id}_${i}`}
                      onSelect={() => runCommand(() => navigate((IR_TEXTO[r.tipo] || IR_TEXTO.tarea)(r)))}
                      className={itemCls}
                      forceMount
                    >
                      <span className="flex-shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">{ETIQUETA_TEXTO[r.tipo] || r.tipo}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{r.label}</div>
                        <div className="text-[12px] text-muted-foreground/70 truncate">{r.snippet || r.sub}</div>
                      </div>
                      <ArrowRight className="h-3 w-3 text-muted-foreground/40 flex-shrink-0" />
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
              {textoResults.length > 0 && <Command.Separator className="my-1.5 h-px bg-border/50 mx-1" />}

              {/* Semantic AI results — solo si hay query y resultados */}
              {semanticResults.length > 0 && (
                <Command.Group
                  heading="Búsqueda IA"
                  className={groupCls}
                  forceMount
                >
                  {semanticResults.map((r, i) => (
                    <Command.Item
                      key={`sem-${r.entidad_id}-${i}`}
                      value={`__semantic_${r.entidad_id}_${i}`}
                      onSelect={() => openSemanticResult(r)}
                      className={itemCls}
                      forceMount
                    >
                      <span className="flex items-center justify-center w-5 h-5 rounded-md bg-[#8B5CF6]/15 ring-1 ring-[#8B5CF6]/20 flex-shrink-0">
                        {r.entidad === "tarea" ? <List className="h-3 w-3 text-[#7C3AED]" /> :
                         r.entidad === "cliente" ? <Users className="h-3 w-3 text-[#7C3AED]" /> :
                         <FileText className="h-3 w-3 text-[#7C3AED]" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{(r.contenido || "").slice(0, 80) || r.entidad_id}</div>
                        <div className="text-[12px] text-muted-foreground/60 truncate">
                          {r.entidad}{r.score != null && r.score < 1 ? ` · ${(r.score * 100).toFixed(0)}% de afinidad` : ""}
                        </div>
                      </div>
                      <ArrowRight className="h-3 w-3 text-muted-foreground/40 flex-shrink-0" />
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {semanticResults.length > 0 && (
                <Command.Separator className="my-1.5 h-px bg-border/50 mx-1" />
              )}

              {/* Navigation pages — leídas del registry (filtradas por rol) */}
              {groupsInOrder.map(groupName => (
                <Command.Group key={groupName} heading={groupName} className={groupCls}>
                  {navItems.filter(item => item.group === groupName).map(item => {
                    const Icon = item.icon;
                    return (
                      <Command.Item
                        key={item.to}
                        value={`${item.label} ${item.group} ${item.to}`}
                        onSelect={() => runCommand(() => { trackNav(item.to, "cmdk"); navigate(item.to); })}
                        className={itemCls}
                      >
                        {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                        <span className="flex-1 truncate">{item.label}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground/40 flex-shrink-0" />
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ))}

              <Command.Separator className="my-1.5 h-px bg-border/50 mx-1" />

              {/* Quick actions */}
              <Command.Group heading="Acciones" className={groupCls}>
                {ACTION_ITEMS.map(item => (
                  <Command.Item
                    key={item.action ?? item.href}
                    value={item.label}
                    onSelect={() => runCommand(() => {
                      if (item.href) navigate(item.href);
                      else if (item.action) {
                        window.dispatchEvent(new CustomEvent("crew-action", { detail: item.action }));
                      }
                    })}
                    className={itemCls}
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-md bg-accent/15 ring-1 ring-accent/20 flex-shrink-0">
                      <item.icon className="h-3 w-3 text-accent" />
                    </span>
                    <span className="flex-1">{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>

            {/* Footer hints */}
            <div className="hidden sm:flex border-t border-border px-4 py-2 items-center gap-4 text-[12px] text-muted-foreground/50 select-none">
              <span className="flex items-center gap-1">
                <kbd className={kbdCls}>↑</kbd><kbd className={kbdCls}>↓</kbd>
                <span className="ml-1">navegar</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className={kbdCls}>↵</kbd>
                <span className="ml-1">ir</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className={kbdCls}>ESC</kbd>
                <span className="ml-1">cerrar</span>
              </span>
              <span className="ml-auto flex items-center gap-1">
                <kbd className={kbdCls}>⌘</kbd><kbd className={kbdCls}>K</kbd>
                <span className="ml-1">abrir</span>
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ── Style constants ────────────────────────────────────────────────────── */
const groupCls = [
  "[&_[cmdk-group-heading]]:px-2",
  "[&_[cmdk-group-heading]]:py-1.5",
  "[&_[cmdk-group-heading]]:text-[13px]",
  "max-sm:[&_[cmdk-group-heading]]:pt-3",
    "[&_[cmdk-group-heading]]:font-extrabold",
  "[&_[cmdk-group-heading]]:text-[#6B6F7A]",
  "[&_[cmdk-group-heading]]:select-none",
].join(" ");

const itemCls = [
  "flex items-center gap-3 px-3 min-h-11 py-1.5 rounded-2xl text-[15px] font-semibold cursor-pointer max-sm:min-h-[56px] max-sm:text-[16px]",
  "text-foreground/80 transition-colors",
  "data-[selected=true]:bg-[#E0F7FB] data-[selected=true]:text-[#0E7490]",
  "outline-none",
].join(" ");

const kbdCls = "border border-border/50 rounded px-1 py-px font-mono text-[12px] bg-muted/40";
